import { formatDate } from "@clipboard-health/date-time";
import { getTheme } from "@clipboard-health/ui-theme";
import { isDefined } from "@clipboard-health/util-ts";
import { type Theme, ThemeProvider } from "@mui/material";
import { get } from "@src/appV2/api";
import { invalidateClipboardScoreProfile } from "@src/appV2/ClipboardScore/api/invalidateClipboardScoreProfile";
import {
  type ClipboardScoreProfileResponse,
  clipboardScoreProfileResponseSchema,
  getClipboardScoreProfileQueryKey,
} from "@src/appV2/ClipboardScore/api/useGetClipboardScoreProfile";
import { useToast } from "@src/appV2/lib";
import { logError } from "@src/appV2/lib/analytics";
import { useCurrentDate } from "@src/appV2/lib/utils/useCurrentDate";
import { RestrictedBottomSheet } from "@src/appV2/redesign/ClipboardScore/components/BottomSheets/RestrictedBottomSheet";
import { PageWrapper } from "@src/appV2/redesign/components/PageWrapper";
import { useCancelShift } from "@src/appV2/Shifts/Shift/ShiftState/api/useCancelShift";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useHistory, useParams } from "react-router-dom";

import { MY_SHIFTS_FULL_PATH } from "../path";
import { useShiftCancelFlow } from "./hooks/useShiftCancelFlow";
import { useShiftCancelPageData } from "./hooks/useShiftCancelPageData";
import {
  ShiftCancelPageConfirmationScreen,
  type ShiftCancelPageConfirmedData,
} from "./ShiftCancelPageConfirmationScreen";
import { ShiftCancelPageContent } from "./ShiftCancelPageContent";
import { ShiftCancelRestrictionWarningScreen } from "./ShiftCancelRestrictionWarningScreen";

/**
 * This component is designed around emergency credits, and should only be invoked when the EC feature is enabled.
 * To avoid errors, it will gracefully degrade in the event that `emergencyCreditStats` is undefined. The resulting
 * behavior should be similar to the the standard bottom-sheet cancel flow, but has not been rigorously designed or tested.
 */
export function ShiftCancelPage(): JSX.Element {
  const { shiftId } = useParams<{ shiftId: string }>();
  const history = useHistory();
  const currentDate = useCurrentDate(60_000);
  const toast = useToast();

  const { userId, tmz } = useDefinedWorker();
  const queryClient = useQueryClient();

  const { mutateAsync: cancelShift } = useCancelShift();

  // Disables data fetching after cancellation succeeds, preventing queries against a cancelled shift.
  const [isCancellationComplete, setIsCancellationComplete] = useState(false);

  const cancelShiftPageData = useShiftCancelPageData({
    shiftId,
    currentDate,
    enabled: !isCancellationComplete,
  });

  const pageDataReady =
    cancelShiftPageData.enabled && !cancelShiftPageData.isLoading && !cancelShiftPageData.isError;

  const flow = useShiftCancelFlow<ShiftCancelPageConfirmedData>({
    isBecomingRestricted: pageDataReady && cancelShiftPageData.isBecomingRestricted,
    onExecuteCancellation: async () => {
      if (!pageDataReady) {
        logError("ShiftCancelPage: handlePressAndHoldToCancel called in unexpected state", {
          error: new Error("cancelShiftPageData was not in a success state"),
        });
        toast.showErrorToast("The cancel button is broken. Please restart the app and try again.");
        throw new Error("cancelShiftPageData was not in a success state");
      }

      const { finalScore, isWithinECWindow, finalECStats, isFinalRestriction, reEnrollmentScore } =
        cancelShiftPageData;

      try {
        await cancelShift({
          shiftId,
          reasonType: "NONE",
          reasonDescription: "",
          isNative: false,
        });
        invalidateClipboardScoreProfile(queryClient, userId);
        setIsCancellationComplete(true);

        return {
          finalScore,
          isWithinECWindow,
          finalECStats,
          isBecomingRestricted: cancelShiftPageData.isBecomingRestricted,
          isAlreadyRestricted: cancelShiftPageData.isWorkerRestricted,
          isFinalRestriction,
          reEnrollmentScore,
        };
      } catch {
        toast.showErrorToast("Something went wrong while cancelling your shift. Please try again.");
        throw new Error("cancellation failed");
      }
    },
    onFetchRestrictionData: async () => {
      try {
        const queryKey = getClipboardScoreProfileQueryKey(userId);
        const profile = await queryClient.fetchQuery<ClipboardScoreProfileResponse>({
          queryKey,
          queryFn: async () => {
            const response = await get({
              url: queryKey[0],
              responseSchema: clipboardScoreProfileResponseSchema,
            });
            return response.data;
          },
        });
        const reEnrollmentDate = profile.accountStatus?.reEnrollmentDate;
        if (isDefined(reEnrollmentDate)) {
          return { liftTime: formatDate(reEnrollmentDate, { timeZone: tmz }) };
        }
      } catch {
        // Graceful degradation: show restricted screen without liftTime
      }
      return {};
    },
    onExit: () => {
      history.push(MY_SHIFTS_FULL_PATH);
    },
    onKeepShift: () => {
      history.goBack();
    },
  });

  const canUseCredit = pageDataReady && cancelShiftPageData.canUseCredit;
  const restrictionDurationDays = pageDataReady
    ? cancelShiftPageData.restrictionDurationDays
    : undefined;

  return (
    <ThemeProvider theme={getTheme()}>
      <PageWrapper sx={{ background: (theme: Theme) => theme.palette.shift?.unverified?.gradient }}>
        {flow.currentStep === "content" && (
          <ShiftCancelPageContent
            {...cancelShiftPageData}
            isCancelling={flow.isCancelling}
            onKeepShift={flow.handleKeepShift}
            onPressAndHoldToCancel={flow.handlePressAndHoldToCancel}
          />
        )}

        {flow.currentStep === "restriction-warning" && (
          <ShiftCancelRestrictionWarningScreen
            canUseCredit={canUseCredit}
            restrictionDurationDays={restrictionDurationDays}
            isCancelling={flow.isCancelling}
            onPressAndHoldToCancel={flow.handleRestrictionWarningCancel}
            onKeepShift={flow.handleKeepShift}
          />
        )}

        {flow.currentStep === "confirmed" && isDefined(flow.confirmedData) && (
          <ShiftCancelPageConfirmationScreen
            {...flow.confirmedData}
            isLoading={flow.isLoadingRestrictionData}
            onContinue={flow.handleConfirmationContinue}
          />
        )}

        {flow.currentStep === "restricted" &&
          isDefined(flow.confirmedData) &&
          isDefined(flow.confirmedData.reEnrollmentScore) && (
            <RestrictedBottomSheet
              modalState={{
                modalIsOpen: true,
                modalIsClosed: false,
                modalStatus: "open" as const,
                setModalStatus: () => {},
                openModal: () => {},
                closeModal: flow.handleRestrictedContinue,
              }}
              isFinalRestriction={flow.confirmedData.isFinalRestriction}
              liftTime={flow.liftTime}
              reEnrollmentScore={flow.confirmedData.reEnrollmentScore}
              onContinue={flow.handleRestrictedContinue}
            />
          )}
      </PageWrapper>
    </ThemeProvider>
  );
}
